import React, { useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { NavItem } from '../../types/content';
interface DesktopNavItemProps {
  item: NavItem;
  solid: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function DesktopNavItem({
  item,
  solid,
  open,
  onOpenChange
}: DesktopNavItemProps) {
  const {
    pathname
  } = useLocation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const paths = item.children ? item.children.map(c => c.to) : [item.to];
  const active = paths.some(p => pathname === p || pathname.startsWith(`${p}/`));
  const base = `relative inline-flex h-10 items-center gap-1 rounded-full px-3.5 text-sm font-medium transition-colors duration-150 ${solid ? active ? 'text-plum' : 'text-plum/70 hover:text-plum' : active ? 'text-porcelain' : 'text-porcelain/75 hover:text-porcelain'}`;
  const indicator = <span aria-hidden="true" className={`absolute inset-x-3.5 bottom-1 h-px origin-left bg-copper transition-transform duration-200 ease-out ${active ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-299" data-mp="1gqftmkgx6xrd" />;
  if (!item.children) {
    return <li data-id="element-300" data-mp="16yidczb21fls">
        <NavLink to={item.to} className={base} data-id="element-301" data-mp="16y0bnn5mi96h">
          {item.label}
          {indicator}
        </NavLink>
      </li>;
  }
  return <li className="relative" onMouseEnter={() => onOpenChange(true)} onMouseLeave={() => onOpenChange(false)} onBlur={e => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onOpenChange(false);
  }} onKeyDown={e => {
    if (e.key === 'Escape') {
      onOpenChange(false);
      buttonRef.current?.focus();
    }
  }} data-id="element-302" data-mp="x2fec2bi1x32">
      <button ref={buttonRef} type="button" aria-expanded={open} aria-haspopup="true" onClick={() => onOpenChange(!open)} className={base} data-id="element-303" data-mp="2iegulqgdz7y3">
        {item.label}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-304" data-mp="br9jgnvtymql" />
        {indicator}
      </button>
      <AnimatePresence data-id="element-305" data-mp="gjar1wi1zpaa">
        {open && <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        x: '-50%'
      }} className="absolute left-1/2 top-full w-80 pt-3" data-id="element-306" data-mp="1tlmdrar5359">
            <ul className="rounded-2xl border border-plum/10 bg-porcelain p-2 shadow-[0_24px_60px_-20px_rgba(33,28,34,0.35)]" data-id="element-307" data-mp="zfsmop00w9bt">
              {item.children.map(child => <li key={child.to} data-id="element-308" data-mp="pi4sc06bx3u6">
                  <NavLink to={child.to} end className={({
              isActive
            }) => `block rounded-xl px-4 py-3 transition-colors duration-150 hover:bg-limestone/70 ${isActive ? 'bg-limestone/70' : ''}`} data-id="element-309" data-mp="ph34sw84movk">
                    <span className="block text-sm font-semibold text-plum" data-id="element-310" data-mp="1sdd21kdeixi5">{child.label}</span>
                    {child.description && <span className="mt-0.5 block text-xs text-plum/60" data-id="element-311" data-mp="21byfj140nmc2">{child.description}</span>}
                  </NavLink>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </li>;
}